import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import {
  type ClipboardScorePolicy,
  type ScoreFreezeReason,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { type ActivityItem, ActivityList } from "@src/appV2/redesign/components/ActivityList";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { SHIFT_DISCOVERY_WORKPLACE_POLICIES_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { differenceInMinutes, parseISO } from "date-fns";
import { generatePath } from "react-router-dom";

import { getClockInSuccessDescription } from "../../Timekeeping/TimekeepingActions/utils/getClockInSuccessDescription";
import { type ProvisionalExtraWorkedTimeRequest } from "./api/useGetProvisionalExtraWorkedTimeRequest";

const LATE_CLOCK_IN_THRESHOLD_MINUTES = 15;

interface TimekeepingActivityListProps {
  arrivedDate?: Date;
  clockInDate?: Date;
  breakStartDate?: Date;
  breakEndDate?: Date;
  workerStage: WorkerStage;
  workplaceId: string;
  workerSubstage?: string;
  workplaceTimezone: string;
  workplaceImage?: string;
  workplaceName?: string;
  mandatoryBreakDurationInMinutes: number | undefined;
  shiftStartDate?: Date;
  shiftEndDate?: Date;
  hasWorkerClockedInOnTime?: boolean;
  isSecondShiftInDouble?: boolean;
  targetArrivalTime?: Date;
  isTimesheetEditDisabled?: boolean;
  shouldEnforceNfcTimesheetBounds?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  shiftBookedAt?: Date;
  policy?: ClipboardScorePolicy;
  isClipboardScoreFreezeEnabled?: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  adjustedClockInDate?: Date;
  onEditClockIn?: () => void;
  isExtendShiftEnabled?: boolean;
  shiftExtensionRequest?: ProvisionalExtraWorkedTimeRequest;
}

export function TimekeepingActivityList(props: TimekeepingActivityListProps) {
  const {
    arrivedDate,
    clockInDate,
    breakStartDate,
    breakEndDate,
    workerStage,
    workplaceId,
    workerSubstage,
    workplaceTimezone,
    workplaceImage,
    workplaceName,
    mandatoryBreakDurationInMinutes,
    hasWorkerClockedInOnTime = false,
    isSecondShiftInDouble = false,
    targetArrivalTime,
    shiftStartDate,
    shiftEndDate,
    shiftBookedAt,
    isTimesheetEditDisabled = false,
    shouldEnforceNfcTimesheetBounds = false,
    isArrivalTimeFeatureEnabled = false,
    policy,
    isClipboardScoreFreezeEnabled = false,
    clipboardScoreFreezeReason = undefined,
    adjustedClockInDate,
    onEditClockIn,
    isExtendShiftEnabled = false,
    shiftExtensionRequest,
  } = props;

  const currentDate = useCurrentDate(1000);

  const showClipboardScoreSubtitle =
    isDefined(clockInDate) &&
    differenceInMinutes(currentDate, clockInDate) < 10 &&
    !isDefined(breakStartDate) &&
    !isDefined(breakEndDate);
  const clipboardScoreSubtitle = showClipboardScoreSubtitle
    ? getClockInSuccessDescription({
        earliestGeofenceEntryTime: arrivedDate,
        clockInTime: clockInDate,
        timezone: workplaceTimezone,
        hasWorkerClockedInOnTime,
        isSecondShiftInDouble,
        isTimesheetEditDisabled,
        shouldEnforceNfcTimesheetBounds,
        isArrivalTimeFeatureEnabled,
        targetArrivalTime,
        shiftStartDate,
        shiftBookedAt,
        policy,
        workerStage,
        workerSubstage,
        isClipboardScoreFreezeEnabled,
        clipboardScoreFreezeReason,
      })
    : undefined;

  const canEditTimesheet = !isTimesheetEditDisabled && !shouldEnforceNfcTimesheetBounds;
  const isLateClockIn =
    isDefined(clockInDate) &&
    isDefined(shiftStartDate) &&
    differenceInMinutes(clockInDate, shiftStartDate) >= LATE_CLOCK_IN_THRESHOLD_MINUTES;
  const lateClockInSubtitle =
    isLateClockIn && canEditTimesheet && !isDefined(onEditClockIn)
      ? "Late clock in can be corrected when you submit your timesheet."
      : undefined;

  const hasClockInAdjustment = isDefined(adjustedClockInDate);
  const effectiveClockInDate = hasClockInAdjustment ? adjustedClockInDate : clockInDate;

  const originalClockInSubtitle =
    hasClockInAdjustment && isDefined(clockInDate) ? (
      <>
        {"Original clock in: "}
        <Box component="span" sx={{ color: "text.primary" }}>
          {formatTimeWithTimezone(clockInDate, workplaceTimezone, false)}
        </Box>
      </>
    ) : undefined;

  const workplacePoliciesUrl = generatePath(SHIFT_DISCOVERY_WORKPLACE_POLICIES_FULL_PATH, {
    workplaceId,
  });

  const activities: ActivityItem[] = [
    {
      id: "arrived",
      icon: isDefined(workplaceImage)
        ? { type: "image", src: workplaceImage, alt: workplaceName }
        : { type: "icon", iconType: "map-pin" },
      title: "Arrived",
      timestamp: arrivedDate ? formatTimeWithTimezone(arrivedDate, workplaceTimezone, false) : "-",
    },
    {
      id: "clock-in",
      icon: { type: "icon", iconType: "clock-filled" },
      title: hasClockInAdjustment ? "Adjusted clock in" : "Clock in",
      timestamp: isDefined(effectiveClockInDate)
        ? formatTimeWithTimezone(effectiveClockInDate, workplaceTimezone, false)
        : "-",
      subtitle: [originalClockInSubtitle, clipboardScoreSubtitle, lateClockInSubtitle].filter(
        isDefined
      ),
      onEdit: onEditClockIn,
    },
  ];

  if (!isDefined(breakStartDate) && mandatoryBreakDurationInMinutes) {
    activities.push({
      id: "break-reminder",
      icon: { type: "icon", iconType: "break" },
      title: "Break reminder",
      subtitle: `Make sure to take your mandatory ${mandatoryBreakDurationInMinutes}-min break`,
      timestamp: "",
    });
  }

  if (isDefined(breakStartDate)) {
    activities.push({
      id: "break-start",
      icon: { type: "icon", iconType: "break" },
      title: "Break start",
      timestamp: formatTimeWithTimezone(breakStartDate, workplaceTimezone, false),
    });
  }

  if (isDefined(breakEndDate)) {
    activities.push({
      id: "break-end",
      icon: { type: "icon", iconType: "break" },
      title: "Break end",
      timestamp: formatTimeWithTimezone(breakEndDate, workplaceTimezone, false),
    });
  }

  if (isExtendShiftEnabled && isDefined(shiftEndDate)) {
    if (isDefined(shiftExtensionRequest)) {
      activities.push({
        id: "shift-extended",
        icon: { type: "icon", iconType: "clock-filled" },
        title: "Shift extended",
        subtitle: ["Additional time will be reviewed and paid separately by the workplace."],
        timestamp: formatTimeWithTimezone(
          parseISO(shiftExtensionRequest.attributes.createdAt),
          workplaceTimezone,
          false
        ),
      });
    } else {
      activities.push({
        id: "scheduled-clock-out",
        icon: { type: "icon", iconType: "clock-filled" },
        title: "Scheduled clock out",
        subtitle: [
          "Additional time will be reviewed and paid out separately by the workplace.",
          <TextButton key="learn-more" href={workplacePoliciesUrl}>
            Learn more.
          </TextButton>,
        ],
        timestamp: formatTimeWithTimezone(shiftEndDate, workplaceTimezone, false),
      });
    }
  }

  return <ActivityList activities={activities} />;
}
